import * as React from 'react';
import { memo } from 'react';
import { itemClassDefault } from "../../custom/render/renderItemCustom.tsx";
import renderSectionCustom, {
	flexClassDefault,
	itemLayoutClassDefault,
	sectionCenteringWrapperClassDefault,
	sectionClassDefault,
	sectionInnerClassDefault
} from "../../custom/render/renderSectionCustom";
import { ContentRenderMode } from "../enums.ts";
import type { PageSectionData } from "../page/content.types";
import {
	getBgColorClass,
	getBgImageClass,
	getMarginClasses,
	getPaddingClasses,
	isSectionWithVideo,
	responsiveHelper
} from "./renderFunctions.ts";

type Props = Readonly<{
	section: PageSectionData
	mode?: ContentRenderMode,
	indexSection?: number
}>

export type RenderClasses = {
	section: string[];
	sectionCenteringWrapper: string[];
	item: string[];
	sectionBgImage: string[];
	sectionInner: string[];
	itemLayout: string[];
	flex: string[]
}

function RenderSection({section, indexSection, mode = ContentRenderMode.NORMAL}: Props) {
	section.visible ??= true;

	if (section.visible === false) {
		return <></>;
	}

	let classes: RenderClasses = {
		section: [...sectionClassDefault],
		sectionCenteringWrapper: [...sectionCenteringWrapperClassDefault],
		sectionInner: [...sectionInnerClassDefault],
		itemLayout: [...itemLayoutClassDefault],
		flex: [...flexClassDefault],
		sectionBgImage: [...getBgImageClass(section)],
		item: [...itemClassDefault],
	}

	if (section?.maxWidth) {
		if (section?.maxWidth !== 'w-full') {
			classes.sectionCenteringWrapper.push('max-w-screen-' + section.maxWidth);
		}
	}

	if (section?.class) {
		classes.sectionInner.push(section.class);
	}

	if (section?.gap) {
		classes.itemLayout = classes.itemLayout.concat(responsiveHelper(section.gap, 'px-', true));
		classes.flex = classes.flex.concat(responsiveHelper(section.gap, '-mx-', true));
	}

	if (section?.textAlign) {
		classes.sectionInner = classes.sectionInner.concat(responsiveHelper(section.textAlign));
	}

	if (section?.alignItems) {
		classes.sectionInner = classes.sectionInner.concat(responsiveHelper(section.alignItems));
	}

	if (section?.justifyContent) {
		classes.sectionInner = classes.sectionInner.concat(responsiveHelper(section.justifyContent));
	}

	if (isSectionWithVideo(section) === false) {
		if (section.bleedBackgroundImage) { // if image bleeds add the image style to whole section
			classes.section = classes.section.concat(classes.sectionBgImage);
		} else {
			classes.sectionInner = classes.sectionInner.concat(classes.sectionBgImage);
		}
	}

	if (section.bleedBackgroundColor) { // if color bleeds add the color class to whole section
		classes.section = classes.section.concat(getBgColorClass(section));
	} else {
		classes.sectionInner = classes.sectionInner.concat(getBgColorClass(section));
	}

	if (section?.textClass) {
		classes.sectionInner.push(section.textClass);
	} else if (section.textColor) {
		classes.sectionInner.push('text-[' + section.textColor + ']');
	}

	classes.section = classes.section.concat(getMarginClasses(section));

	if (mode !== ContentRenderMode.REUSE_BLOCK) {
		classes.sectionInner = classes.sectionInner.concat(getPaddingClasses(section));
	}

	return renderSectionCustom(section, mode, indexSection, classes);
}

export default memo(RenderSection);
